/**
 * 54. Products — product list/detail; variants, UoM, valuation, tax,
 * status, barcode.
 *
 * Arabic and English (C7): every word from the catalogs; a product's type,
 * valuation method and status are the glossary's words, never the stored
 * value. Units of measure are the workspace's own data.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList, useApiQuery } from "@/lib/api/hooks";
import { CreateRecordButton } from "@/components/CreateRecordButton";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel, valueLabel } from "@/lib/i18n/records";
import { formatQuantity } from "@/lib/money";

interface ProductRow {
  id: string;
  sku: string;
  name: string;
  productType: "Item" | "Service" | "Expense";
  unitOfMeasure: string;
  valuationMethod: string | null;
  barcode: string | null;
  variantCount: number;
  status: "Draft" | "Active" | "Obsolete";
}

interface LookupOption {
  id: string;
  name?: string;
  code?: string;
  sku?: string;
}

const PRODUCT_TYPES = ["Item", "Service", "Expense"] as const;

export function ProductsPage() {
  const { t } = useTranslation();
  const uoms = useApiQuery<LookupOption[]>(["uoms"], "/uoms?limit=100");
  const { data, isLoading } = useApiList<ProductRow>(
    ["inventory", "products"],
    "/inventory/products",
  );

  return (
    <div>
      <PageHeader
        title={t("inventory.products.title")}
        subtitle={t("inventory.products.subtitle")}
        actions={
          <CreateRecordButton
            label={t("inventory.products.new")}
            path="/products"
            invalidate={["inventory", "products"]}
            fields={[
              { name: "sku", label: t("fields.sku"), required: true },
              { name: "name", label: t("fields.name"), required: true },
              {
                name: "unitOfMeasureId",
                label: t("inventory.products.unitOfMeasure"),
                type: "select",
                required: true,
                options: (uoms.data ?? []).map((u) => ({
                  value: u.id,
                  label: u.code
                    ? `${u.code} — ${u.name ?? ""}`
                    : (u.name ?? u.id),
                })),
              },
              {
                name: "productType",
                label: t("fields.type"),
                type: "select",
                required: true,
                defaultValue: "Item",
                options: PRODUCT_TYPES.map((type) => ({
                  value: type,
                  label: t(`inventory.products.typeOptions.${type}`),
                })),
              },
            ]}
          />
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("inventory.products.list"), data.total)}>
          <DataTable<ProductRow>
            rows={data.items}
            columns={[
              {
                key: "sku",
                label: t("fields.sku"),
                render: (r) => <span className="atlas-mono">{r.sku}</span>,
              },
              { key: "name", label: t("fields.name") },
              {
                key: "productType",
                label: t("fields.type"),
                render: (r) =>
                  r.productType
                    ? valueLabel(t, "productType", r.productType)
                    : "—",
              },
              { key: "unitOfMeasure", label: t("inventory.products.uom") },
              {
                key: "valuationMethod",
                label: t("inventory.products.valuation"),
                render: (r) =>
                  r.valuationMethod
                    ? valueLabel(t, "valuationMethod", r.valuationMethod)
                    : "—",
              },
              {
                key: "barcode",
                label: t("inventory.products.barcode"),
                render: (r) =>
                  r.barcode ? (
                    <span className="atlas-mono">{r.barcode}</span>
                  ) : (
                    "—"
                  ),
              },
              {
                key: "variantCount",
                label: t("inventory.products.variants"),
                numeric: true,
                render: (r) => formatQuantity(r.variantCount, 0),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) =>
                  r.status === "Active" ? (
                    <StatusBadge status={r.status} type="Product" />
                  ) : (
                    <Badge
                      tone={r.status === "Obsolete" ? "danger" : "default"}
                    >
                      {statusLabel(t, "Product", r.status)}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
